'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { renderResumeHtml } from '@/lib/resume/templates/renderer';
import { ResumeDocumentPreview } from '@/components/features/resume/ResumeDocumentPreview';
import {
  beginInPlaceEdit,
  type InlineEditTarget,
} from '@/components/features/resume/attachInlinePreviewEditing';
import { parseFieldEdit, readFieldValue, textToSkills } from '@/lib/resume/inline-edit';
import type { InPlaceEditSession } from '@/components/features/resume/attachInlinePreviewEditing';
import type { ResumeDocumentPreviewHandle } from '@/components/features/resume/ResumeDocumentPreview';
import type { InlineDateTarget } from '@/components/features/resume/attachInlinePreviewEditing';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Button } from '@/components/ui/forms/button';
import { Textarea } from '@/components/ui/forms/textarea';
import { PreviewAnnotations } from './PreviewAnnotations';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

/**
 * Coerce a stored date into what `<input type="month">` accepts (`YYYY-MM`).
 *
 * Stored values are inconsistent across resumes — some are already `YYYY-MM`,
 * some full ISO dates, some free text carried over from an imported PDF.
 * Anything unreadable becomes empty rather than a wrong date the user might
 * not notice.
 */
export function toMonthValue(value: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(value.trim());
  return match ? `${match[1]}-${match[2]}` : '';
}

/** Open date picker for one record's date range. */
interface DateEdit {
  section: string;
  id: string;
  single: boolean;
  startDate: string;
  endDate: string;
  isCurrentlyWorking: boolean;
}

interface ResumePreviewProps {
  defaultToFit?: boolean;
  controlsFixedToViewport?: boolean;
  /** Latest analysis report's suggestions, for the inline comment markers. */
  analysisSuggestions?: ResumeAnalysisSuggestion[];
  /** Whether the inline comment markers are currently shown. */
  showAnalysisComments?: boolean;
  /** Applies one suggestion from its inline comment popover. */
  onApplySuggestion?: (suggestion: ResumeAnalysisSuggestion) => void;
  /** id of the suggestion currently being applied, for its loading state. */
  applyingSuggestionId?: string | null;
}

interface MobileEdit {
  path: string;
  label: string;
  value: string;
  caretOffset: number;
}

/** Human label for the field being edited, so the sheet says what it is. */
function labelForPath(path: string): string {
  if (path === 'profile.headline') return 'Headline';
  if (path === 'profile.summary') return 'Professional summary';
  if (path === 'personalInfo.firstName') return 'First name';
  if (path === 'personalInfo.lastName') return 'Last name';
  if (path === 'contact.email') return 'Email';
  if (path === 'contact.phone') return 'Phone';
  if (path === 'contact.location') return 'Location';
  if (path === 'skills.technical') return 'Technical skills';
  if (path === 'skills.soft') return 'Soft skills';
  if (path.endsWith('.skills')) return 'Skills';
  if (path.endsWith('.institution')) return 'Institution';
  if (path.endsWith('.degree')) return 'Degree';
  if (path.endsWith('.field')) return 'Field of study';
  if (path.endsWith('.gpa')) return 'GPA';
  if (path.endsWith('.issuer')) return 'Issuer';
  if (path.endsWith('.credentialUrl')) return 'Credential URL';
  if (path.endsWith('.name')) return 'Name';
  if (path.endsWith('.title')) return 'Title';
  if (path.endsWith('.company')) return 'Company';
  if (path.endsWith('.responsibilities')) return 'Responsibilities';
  if (path.endsWith('.content')) return 'Content';
  return 'Text';
}

export default function ResumePreview({
  defaultToFit = false,
  controlsFixedToViewport = false,
  analysisSuggestions,
  showAnalysisComments = false,
  onApplySuggestion,
  applyingSuggestionId,
}: ResumePreviewProps) {
  const {
    resume,
    updateContent,
    updateEmployment,
    updateEducation,
    updateCertification,
    updateCustomSection,
    previewTemplateId,
    previewColorSchemeId,
  } = useResumeBuilderStore();
  const [mobileEdit, setMobileEdit] = useState<MobileEdit | null>(null);
  const [dateEdit, setDateEdit] = useState<DateEdit | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sessionRef = useRef<InPlaceEditSession | null>(null);
  const previewHandleRef = useRef<ResumeDocumentPreviewHandle | null>(null);
  const annotationsContainerRef = useRef<HTMLDivElement>(null);
  const [isEditingInPlace, setIsEditingInPlace] = useState(false);
  /** Whether this edit zoomed in, so we only bounce back to Fit if it did. */
  const zoomedForEditRef = useRef(false);

  /**
   * The Style selector's hover preview overrides the saved template/color
   * scheme here rather than in the store's `resume.settings`, so it never
   * touches `hasUnsavedChanges` or triggers autosave — see
   * `previewTemplateId`/`previewColorSchemeId` on the store.
   */
  const effectiveSettings = useMemo(() => {
    if (!resume) return null;
    if (previewTemplateId == null && previewColorSchemeId == null) return resume.settings;
    return {
      ...resume.settings,
      templateId: previewTemplateId ?? resume.settings.templateId,
      colorSchemeId: previewColorSchemeId ?? resume.settings.colorSchemeId,
    };
  }, [resume, previewTemplateId, previewColorSchemeId]);

  const html = useMemo(() => {
    if (!resume || !effectiveSettings) return '';
    return renderResumeHtml(resume.content, effectiveSettings);
  }, [resume, effectiveSettings]);

  /** Route one field's new text to the matching store action. */
  const commitField = useCallback(
    (path: string, value: string) => {
      const edit = parseFieldEdit(path, value);
      if (!edit || !resume) return;

      const content = resume.content;

      switch (edit.kind) {
        case 'profile':
          updateContent({ profile: { ...content.profile, [edit.field]: value } });
          break;
        case 'personalInfo':
          updateContent({ personalInfo: { ...content.personalInfo, [edit.field]: value } });
          break;
        case 'contact':
          updateContent({ contact: { ...content.contact, [edit.field]: value } });
          break;
        case 'employment':
          updateEmployment(edit.id, { [edit.field]: value });
          break;
        case 'education':
          updateEducation(edit.id, { [edit.field]: value });
          break;
        case 'certification':
          updateCertification(edit.id, { [edit.field]: value });
          break;
        case 'skillList':
          updateContent({
            skills: { ...content.skills, [edit.list]: textToSkills(value) },
          });
          break;
        case 'skillItem': {
          // Clearing a chip removes the skill; that is the only sensible
          // reading of deleting all of its text.
          const next = [...(content.skills?.[edit.list] ?? [])];
          if (edit.index < 0 || edit.index >= next.length) break;
          if (value.trim()) next[edit.index] = value.trim();
          else next.splice(edit.index, 1);
          updateContent({ skills: { ...content.skills, [edit.list]: next } });
          break;
        }
        case 'skillCategoryItem': {
          updateContent({
            skills: {
              ...content.skills,
              categories: (content.skills?.categories ?? []).map((category) => {
                if (category.id !== edit.id) return category;
                const next = [...(category.skills ?? [])];
                if (edit.index < 0 || edit.index >= next.length) return category;
                if (value.trim()) next[edit.index] = value.trim();
                else next.splice(edit.index, 1);
                return { ...category, skills: next };
              }),
            },
          });
          break;
        }
        case 'skillCategory':
          updateContent({
            skills: {
              ...content.skills,
              categories: (content.skills?.categories ?? []).map((category) =>
                category.id === edit.id
                  ? { ...category, skills: textToSkills(value) }
                  : category,
              ),
            },
          });
          break;
        case 'customSection':
          updateCustomSection(edit.id, { [edit.field]: value });
          break;
        case 'date':
          // Dates arrive from the picker, already in the stored format.
          if (edit.owner === 'employment') updateEmployment(edit.id, { [edit.field]: value });
          else if (edit.owner === 'education') updateEducation(edit.id, { [edit.field]: value });
          else updateCertification(edit.id, { [edit.field]: value });
          break;
      }
    },
    [
      resume,
      updateContent,
      updateEmployment,
      updateEducation,
      updateCertification,
      updateCustomSection,
    ]
  );

  const handleRequestEdit = useCallback(
    (target: InlineEditTarget) => {
      if (!resume) return;

      // A whole-list field (rendered as chips) still takes the sheet: reading an
      // in-place edit back would weld every chip onto one line.
      if (target.list) {
        setMobileEdit({
          path: target.path,
          label: labelForPath(target.path),
          value: readFieldValue(resume.content, target.path) ?? '',
          caretOffset: target.caretOffset,
        });
        return;
      }

      sessionRef.current?.cancel();

      setIsEditingInPlace(true);
      sessionRef.current = beginInPlaceEdit(target, {
        /*
         * Note what does NOT happen here: no return to Fit.
         *
         * Blur fires when the next field is tapped, so restoring Fit on commit
         * made every field-to-field move zoom out and straight back in. Staying
         * zoomed lets the next tap simply pan across at the same scale.
         * Returning to Fit is now only what the explicit Save/Cancel do.
         */
        onCommit: (path, value) => {
          sessionRef.current = null;
          setIsEditingInPlace(false);
          commitField(path, value);
        },
        onCancel: () => {
          sessionRef.current = null;
          setIsEditingInPlace(false);
        },
      });

      /*
       * Zoom AFTER starting the session, not before: beginInPlaceEdit focuses
       * the element and places a caret, and setting a selection can scroll the
       * container. Positioning last makes ours the final word.
       *
       * Called unconditionally. This used to be gated on `(pointer: coarse)`,
       * which meant it silently did nothing in a narrow desktop window — and
       * the preview is the better judge anyway: it knows its own scale, so it
       * zooms only when the text is actually too small to edit and just centres
       * the field otherwise.
       */
      zoomedForEditRef.current =
        (previewHandleRef.current?.focusField(target.element) ?? false) ||
        zoomedForEditRef.current;
    },
    [resume, commitField]
  );

  /**
   * Explicit Save from the zoom bar — the touch-friendly way out of an edit.
   *
   * Unlike a blur, this is a deliberate "I am finished", so it is the one place
   * that returns the sheet to Fit.
   */
  const handleSaveInPlace = useCallback(() => {
    sessionRef.current?.commit();
    if (zoomedForEditRef.current) previewHandleRef.current?.fitToView();
    zoomedForEditRef.current = false;
  }, []);

  const handleCancelInPlace = useCallback(() => {
    sessionRef.current?.cancel();
    if (zoomedForEditRef.current) previewHandleRef.current?.fitToView();
    zoomedForEditRef.current = false;
  }, []);

  /**
   * Clicking a rendered date range opens the picker.
   *
   * `path` is the owning record (`employment.<id>`), not a single field: the
   * range is one visual unit, so asking which half was clicked would be guesswork.
   */
  const handleRequestDateEdit = useCallback(
    (target: InlineDateTarget) => {
      if (!resume) return;
      const [section, id] = target.path.split('.');
      if (!section || !id) return;

      const record =
        section === 'employment'
          ? resume.content.employment?.find((item) => item.id === id)
          : section === 'education'
            ? resume.content.education?.find((item) => item.id === id)
            : resume.content.certifications?.find((item) => item.id === id);
      if (!record) return;

      const asDate = (value: unknown) => (typeof value === 'string' ? value : '');
      const entry = record as unknown as Record<string, unknown>;

      setDateEdit({
        section,
        id,
        // Certifications carry a single date; the other two carry a range.
        single: section === 'certifications',
        startDate: asDate(entry.startDate ?? entry.date ?? entry.issueDate),
        endDate: asDate(entry.endDate),
        isCurrentlyWorking: entry.isCurrentlyWorking === true,
      });
    },
    [resume]
  );

  /** Write the picker's values back through the same allowlisted paths. */
  const commitDateEdit = useCallback(
    (next: DateEdit) => {
      if (next.section === 'certifications') {
        commitField(`certifications.${next.id}.date`, next.startDate);
      } else {
        commitField(`${next.section}.${next.id}.startDate`, next.startDate);
        commitField(
          `${next.section}.${next.id}.endDate`,
          next.isCurrentlyWorking ? '' : next.endDate
        );
        if (next.section === 'employment') {
          updateEmployment(next.id, { isCurrentlyWorking: next.isCurrentlyWorking });
        }
      }
      setDateEdit(null);
    },
    [commitField, updateEmployment]
  );

  // Drop the caret where they tapped, rather than at the end of the text.
  useEffect(() => {
    if (!mobileEdit) return;
    const node = textareaRef.current;
    if (!node) return;
    const at = Math.min(mobileEdit.caretOffset, mobileEdit.value.length);
    node.focus();
    node.setSelectionRange(at, at);
  }, [mobileEdit]);

  if (!resume) return null;

  return (
    <>
      <div ref={annotationsContainerRef} className="relative h-full w-full">
        <ResumeDocumentPreview
          html={html}
          templateId={effectiveSettings?.templateId ?? resume.settings.templateId}
          defaultToFit={defaultToFit}
          controlsFixedToViewport={controlsFixedToViewport}
          onRequestEdit={handleRequestEdit}
          onRequestDateEdit={handleRequestDateEdit}
          editing={isEditingInPlace}
          onSaveEdit={handleSaveInPlace}
          onCancelEdit={handleCancelInPlace}
          previewHandleRef={previewHandleRef}
        />
        <PreviewAnnotations
          suggestions={analysisSuggestions ?? []}
          showComments={showAnalysisComments}
          onApply={onApplySuggestion ?? (() => {})}
          applyingSuggestionId={applyingSuggestionId}
          containerRef={annotationsContainerRef}
        />
      </div>

      <Dialog open={Boolean(dateEdit)} onOpenChange={(open) => !open && setDateEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeading
            title={dateEdit?.single ? 'Certification date' : 'Date range'}
            description="Pick the dates instead of typing them."
          />
          {dateEdit ? (
            <div className="space-y-3 px-4 pb-2">
              <label className="block">
                <span className="text-caption text-muted-foreground">
                  {dateEdit.single ? 'Date' : 'Start'}
                </span>
                {/* `month`, not `date`: resumes store and render month
                    precision, so a day picker would invent a precision the
                    document never shows. */}
                <input
                  type="month"
                  autoFocus
                  value={toMonthValue(dateEdit.startDate)}
                  onChange={(event) => setDateEdit({ ...dateEdit, startDate: event.target.value })}
                  className="mt-1 w-full border border-border bg-background px-2 py-1.5 text-body"
                />
              </label>

              {!dateEdit.single && (
                <>
                  <label className="block">
                    <span className="text-caption text-muted-foreground">End</span>
                    <input
                      type="month"
                      value={toMonthValue(dateEdit.endDate)}
                      disabled={dateEdit.isCurrentlyWorking}
                      onChange={(event) => setDateEdit({ ...dateEdit, endDate: event.target.value })}
                      className="mt-1 w-full border border-border bg-background px-2 py-1.5 text-body disabled:opacity-50"
                    />
                  </label>

                  {dateEdit.section === 'employment' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={dateEdit.isCurrentlyWorking}
                        onChange={(event) =>
                          setDateEdit({ ...dateEdit, isCurrentlyWorking: event.target.checked })
                        }
                      />
                      <span className="text-body">I currently work here</span>
                    </label>
                  )}
                </>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => dateEdit && commitDateEdit(dateEdit)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mobileEdit !== null} onOpenChange={(open) => !open && setMobileEdit(null)}>
        <DialogContent>
          <DialogHeading title={mobileEdit?.label ?? 'Edit'} description="Changes apply to your resume." />
          <Textarea
            ref={textareaRef}
            value={mobileEdit?.value ?? ''}
            rows={8}
            onChange={(event) =>
              setMobileEdit((previous) => (previous ? { ...previous, value: event.target.value } : previous))
            }
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMobileEdit(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (mobileEdit) commitField(mobileEdit.path, mobileEdit.value);
                setMobileEdit(null);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
